import { initWebVitals } from '@geauxfile/analytics';
import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';


import { GTM } from './components/GTM';
import { BrandFooter } from './components/shared/BrandFooter';
import { LoadingSpinner } from './components/shared/LoadingSpinner';
import { AdminGuard } from './guards/AdminGuard';
import { AuthGuard } from './guards/AuthGuard';
import { AdminLayout } from './layouts/AdminLayout';
import { AuthenticatedLayout } from './layouts/AuthenticatedLayout';
import { AdminDashboard } from './pages/admin/Dashboard';
import { Dashboard } from './pages/Dashboard';
import { ForgotPassword } from './pages/ForgotPassword';
import { Login } from './pages/Login';
import { Signup } from './pages/Signup';
import { AuthProvider } from './providers/AuthProvider';
import { CartProvider } from './providers/CartProvider';
import { isPurchaseLinkEntry } from './services/checkoutService';

// Lazy-loaded customer pages
const Account = lazy(() => import('./pages/Account').then(m => ({ default: m.Account })));
const ActivateService = lazy(() => import('./pages/ActivateService').then(m => ({ default: m.ActivateService })));
const Checkout = lazy(() => import('./pages/Checkout').then(m => ({ default: m.Checkout })));
const CheckoutConfirmation = lazy(() => import('./pages/CheckoutConfirmation').then(m => ({ default: m.CheckoutConfirmation })));
const Documents = lazy(() => import('./pages/Documents').then(m => ({ default: m.Documents })));
const GuestCheckout = lazy(() => import('./pages/GuestCheckout').then(m => ({ default: m.GuestCheckout })));
const MyOrders = lazy(() => import('./pages/MyOrders').then(m => ({ default: m.MyOrders })));
const OrderDetail = lazy(() => import('./pages/OrderDetail').then(m => ({ default: m.OrderDetail })));
const OrderService = lazy(() => import('./pages/OrderService').then(m => ({ default: m.OrderService })));
const ServiceDetail = lazy(() => import('./pages/ServiceDetail').then(m => ({ default: m.ServiceDetail })));
const Entities = lazy(() => import('./pages/Entities').then(m => ({ default: m.Entities })));
const EntityDetail = lazy(() => import('./pages/EntityDetail').then(m => ({ default: m.EntityDetail })));
const Services = lazy(() => import('./pages/Services').then(m => ({ default: m.Services })));
const Support = lazy(() => import('./pages/Support').then(m => ({ default: m.Support })));
const TicketDetail = lazy(() => import('./pages/TicketDetail').then(m => ({ default: m.TicketDetail })));
const Invoices = lazy(() => import('./pages/Invoices').then(m => ({ default: m.Invoices })));
const InvoiceDetail = lazy(() => import('./pages/InvoiceDetail').then(m => ({ default: m.InvoiceDetail })));
const Notifications = lazy(() => import('./pages/Notifications').then(m => ({ default: m.Notifications })));
const Calendar = lazy(() => import('./pages/Calendar').then(m => ({ default: m.Calendar })));

// Lazy-loaded admin pages
const AdminAnalyticsSocial = lazy(() => import('./pages/admin/AnalyticsSocial').then(m => ({ default: m.AdminAnalyticsSocial })));
const AdminAuditLog = lazy(() => import('./pages/admin/AuditLog').then(m => ({ default: m.AdminAuditLog })));
const BulkUpload = lazy(() => import('./pages/admin/BulkUpload').then(m => ({ default: m.BulkUpload })));
const AdminControlRoom = lazy(() => import('./pages/admin/ControlRoom').then(m => ({ default: m.AdminControlRoom })));
const AdminCustomers = lazy(() => import('./pages/admin/Customers').then(m => ({ default: m.AdminCustomers })));
const AdminDocuments = lazy(() => import('./pages/admin/Documents').then(m => ({ default: m.AdminDocuments })));
const AdminEINRequests = lazy(() => import('./pages/admin/EINRequests').then(m => ({ default: m.AdminEINRequests })));
const AdminEINWizard = lazy(() => import('./pages/admin/EINWizard').then(m => ({ default: m.AdminEINWizard })));
const AdminFulfillment = lazy(() => import('./pages/admin/Fulfillment').then(m => ({ default: m.AdminFulfillment })));
const AdminFulfillmentDetail = lazy(() => import('./pages/admin/FulfillmentDetail').then(m => ({ default: m.AdminFulfillmentDetail })));
const AdminRAAddresses = lazy(() => import('./pages/admin/RAAddresses').then(m => ({ default: m.AdminRAAddresses })));
const AdminRACRequests = lazy(() => import('./pages/admin/RACRequests').then(m => ({ default: m.AdminRACRequests })));
const AdminServices = lazy(() => import('./pages/admin/Services').then(m => ({ default: m.AdminServices })));
const AdminSOSRequests = lazy(() => import('./pages/admin/SOSRequests').then(m => ({ default: m.AdminSOSRequests })));
const AdminSupportPage = lazy(() => import('./pages/admin/Support').then(m => ({ default: m.AdminSupport })));
const AdminSupportTicketDetail = lazy(() => import('./pages/admin/SupportTicketDetail').then(m => ({ default: m.AdminSupportTicketDetail })));
const AdminUsers = lazy(() => import('./pages/admin/Users').then(m => ({ default: m.AdminUsers })));

/**
 * The entry bootstrap applies the host's theme before the application mounts.
 */

function AppLoadingSpinner() {
  return (
    <div className="flex h-screen items-center justify-center">
      <LoadingSpinner size="lg" />
    </div>
  );
}

/**
 *
 */
function CheckoutAccess() {
  const location = useLocation();
  return isPurchaseLinkEntry(location.search, location.hash) ? <Outlet />
    : <AuthGuard><AuthenticatedLayout /></AuthGuard>;
}

/**
 *
 */
export function App() {
  useEffect(() => {
    initWebVitals({ sendToDataLayer: true, sendToRUM: false });
  }, []);

  return (
    <AuthProvider>
      <CartProvider>
        {/* Google Tag Manager */}
        <GTM />
        <Suspense fallback={<AppLoadingSpinner />}>
          <Routes>
            {/* Public routes — dark obsidian theme for brand continuity */}
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/guest-checkout" element={<GuestCheckout />} />
            <Route path="/checkout/confirmation" element={<CheckoutConfirmation />} />
            {/* T9.17: Magic link activation route */}
            <Route path="/activate/:token" element={<ActivateService />} />
            {/* EIN Wizard — unguarded operator tool */}
            <Route path="/ein-wizard" element={<AdminEINWizard />} />

            <Route element={<CheckoutAccess />}>
              <Route path="/checkout" element={<Checkout />} />
            </Route>

            {/* Protected customer routes */}
            <Route
              element={
                <AuthGuard>
                  <AuthenticatedLayout />
                </AuthGuard>
              }
            >
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/services" element={<Services />} />
              <Route path="/services/:serviceId" element={<ServiceDetail />} />
              <Route path="/entities" element={<Entities />} />
              <Route path="/entities/:entityId" element={<EntityDetail />} />
              <Route path="/orders" element={<MyOrders />} />
              <Route path="/orders/:orderId" element={<OrderDetail />} />
              <Route path="/documents" element={<Documents />} />
              <Route path="/invoices" element={<Invoices />} />
              <Route path="/invoices/:id" element={<InvoiceDetail />} />
              <Route path="/support" element={<Support />} />
              <Route path="/support/:ticketId" element={<TicketDetail />} />
              <Route path="/account" element={<Account />} />
              <Route path="/checkout/confirmation" element={<CheckoutConfirmation />} />
              <Route path="/order" element={<OrderService />} />
              <Route path="/notifications" element={<Notifications />} />
              <Route path="/calendar" element={<Calendar />} />
            </Route>

            {/* Admin routes */}
            <Route
              path="/admin"
              element={
                <AdminGuard>
                  <AdminLayout />
                </AdminGuard>
              }
            >
              <Route index element={<AdminDashboard />} />
              <Route path="customers" element={<AdminCustomers />} />
              <Route path="services" element={<AdminServices />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="fulfillment" element={<AdminFulfillment />} />
              <Route path="fulfillment/:id" element={<AdminFulfillmentDetail />} />
              <Route path="sos" element={<AdminSOSRequests />} />
              <Route path="rac" element={<AdminRACRequests />} />
              <Route path="ein" element={<AdminEINRequests />} />
              <Route path="ein-wizard" element={<AdminEINWizard />} />
              <Route path="support" element={<AdminSupportPage />} />
              <Route path="support/:ticketId" element={<AdminSupportTicketDetail />} />
              <Route path="audit-log" element={<AdminAuditLog />} />
              <Route path="control-room" element={<AdminControlRoom />} />
              <Route path="bulk-upload" element={<BulkUpload />} />
              <Route path="ra-addresses" element={<AdminRAAddresses />} />
              <Route path="documents" element={<AdminDocuments />} />
              <Route path="analytics/social" element={<AdminAnalyticsSocial />} />
            </Route>

            {/* Default redirect */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Suspense>
        <BrandFooter />
      </CartProvider>
    </AuthProvider>
  );
}
